import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Users,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import ImagePreview from "@/components/images/image-preview";
import { Id } from "@/convex/_generated/dataModel";

export const Route = createFileRoute(
  "/_super/super/control-panel/$companyId/overview",
)({
  component: CompanyOverview,
});

function CompanyOverview() {
  const { companyId } = Route.useParams();

  const details = useQuery(
    api.companies.subscriptions.queries.getCompanySubscriptionDetails,
    {
      companyId: companyId as Id<"companies">,
    },
  );

  if (!details) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-3 bg-muted rounded w-3/4 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded" />
                  <div className="h-3 bg-muted rounded w-5/6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const { company, subscription, plan, stats } = details;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Información de la Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4">
              {company.logoId && (
                <div className="flex-shrink-0">
                  <ImagePreview
                    imageId={company.logoId}
                    entityName={company.name}
                    entityType="company"
                    size="lg"
                    className="h-16 w-16 rounded-lg"
                  />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{company.name}</h3>
                {company.legalName && (
                  <p className="text-sm text-muted-foreground">
                    {company.legalName}
                  </p>
                )}
                <Badge
                  variant={
                    company.status === "active" ? "default" : "secondary"
                  }
                  className="mt-2"
                >
                  {company.status === "active"
                    ? "Activa"
                    : company.status === "inactive"
                      ? "Inactiva"
                      : "Suspendida"}
                </Badge>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t">
              {company.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{company.email}</span>
                </div>
              )}

              {company.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{company.phone}</span>
                </div>
              )}

              {company.address && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="line-clamp-2">{company.address}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  Creada el{" "}
                  {format(company._creationTime, "PPP", { locale: es })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Suscripción Actual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription && plan ? (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl font-bold">{plan.name}</span>
                    <Badge
                      variant={
                        subscription.status === "active"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {subscription.status === "active"
                        ? "Activa"
                        : subscription.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {plan.description}
                  </p>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Precio mensual
                    </span>
                    <span className="font-medium">
                      {new Intl.NumberFormat("es-HN", {
                        style: "currency",
                        currency: "HNL",
                      }).format(plan.price)}
                    </span>
                  </div>

                  {subscription.trialEnd &&
                    subscription.status === "trialing" && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Próximo pago (fin de prueba)
                        </span>
                        <span className="font-medium">
                          {format(subscription.trialEnd, "PP", {
                            locale: es,
                          })}
                        </span>
                      </div>
                    )}

                  {subscription.status === "trialing" &&
                    subscription.trialEnd && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Fin de prueba
                        </span>
                        <span className="font-medium">
                          {format(subscription.trialEnd, "PP", { locale: es })}
                        </span>
                      </div>
                    )}
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    Módulos habilitados
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {subscription.enabledModules.map((module) => (
                      <Badge key={module} variant="outline" className="text-xs">
                        {module}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Sin suscripción activa
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Estadísticas de Usuarios
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{stats.totalUsers}</span>
                  <span className="text-sm text-muted-foreground">
                    usuarios totales
                  </span>
                </div>
                {subscription && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Límite: {subscription.limits.maxUsers} usuarios
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Información Adicional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {company.taxId && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">RTN/Tax ID</span>
                <span className="font-medium">{company.taxId}</span>
              </div>
            )}

            {company.defaultCurrency && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Moneda por defecto
                </span>
                <span className="font-medium">{company.defaultCurrency}</span>
              </div>
            )}

            {company.defaultTaxRate !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Impuesto por defecto
                </span>
                <span className="font-medium">{company.defaultTaxRate}%</span>
              </div>
            )}

            {company.defaultProfitMargin !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Margen de ganancia
                </span>
                <span className="font-medium">
                  {company.defaultProfitMargin}%
                </span>
              </div>
            )}

            {company.paymentTerms && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Términos de pago</span>
                <span className="font-medium">
                  {company.paymentTerms === "net_30"
                    ? "30 días"
                    : company.paymentTerms === "net_15"
                      ? "15 días"
                      : company.paymentTerms === "due_on_receipt"
                        ? "Vencimiento al recibo"
                        : company.paymentTerms}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
